import * as React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import find from 'lodash/find';
import { AppState } from '@om-fe/core/core/types';
import {
    FilteredEventStatistics,
    FilteredMatchesWithEvents,
    MatchListStoreModel,
} from '../match-list/utils/match-list-models';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import { BreakpointsState } from '@om-fe/core/core/store/breakpoints';
import MatchTime from '../match-time/match-time';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import getSlice from '@om-fe/core/utils/get-slice';
import { createBem, isLiveStatus } from '@om-fe/utils';
import { EventDescriptorMarketsNumber } from '@om-fe/core/components/event-descriptors/event-descriptor-markets-number';
import { EventItemClassName } from './event-item.utils';
import { EventItemCountdown } from '@om-fe/core/components/countdown/event-item-countdown';
import { getTimeUntilMatchStart } from '@om-fe/core/components/countdown/countdown.utils';
import { GeneralIcon } from '@om-fe/widgets/dist/components/icon/general-icon';
import BetRadarStatistics from '@om-fe/core/components/betradar-statistics/betradar-statistics';

export interface MatchInfoDetailsPublicProps {
    readonly sliceKey: string;
    readonly matchId: string;
    readonly matchTimeId?: string;
    readonly remainingTimeId?: string;
    readonly currentEventPartId?: string;
}

interface MatchInfoDetailsStoreProps {
    readonly match?: FilteredMatchesWithEvents;
    readonly isSmall?: boolean;
    readonly matchTime?: number;
    readonly timeLeft?: number;
    readonly currentEventPart?: string;
    readonly hasStreamingProviderId?: boolean;
    readonly currentEventPartParam?: string;
    readonly betRadarEventId?: string;
}

interface MatchInfoDetailsStateProps {
    readonly isCountdownFinished: boolean;
}

export type MatchInfoDetailsProps = MatchInfoDetailsPublicProps
    & MatchInfoDetailsStoreProps
    & I18nContextInterface;

const bem = createBem({
    name: EventItemClassName,
});

class MatchInfoDetailsComponent extends React.PureComponent<MatchInfoDetailsProps, MatchInfoDetailsStateProps> {
    state: MatchInfoDetailsStateProps = {
        isCountdownFinished: false,
    };

    handleCountdownStatus = (): void => {
        this.setState({
            isCountdownFinished: true,
        });
    };

    getMatchTimeNode(): React.ReactNode {
        const { match, matchTime, timeLeft, currentEventPart, currentEventPartParam, isSmall, gettext } = this.props;
        const  timeLeftText  = isSmall ? gettext('Left') : gettext('Time Left');

        if (!match) {
            return null;
        }

        const { statusId, startTime } = match;
        const timeUntilStartInSeconds: number = getTimeUntilMatchStart(startTime);

        if (isLiveStatus(statusId) && timeUntilStartInSeconds > 0) {
            return (
                <EventItemCountdown
                    secondsLeft={timeUntilStartInSeconds}
                    onTimerEnd={this.handleCountdownStatus}
                    bem={bem}
                />
            );
        } else if (isLiveStatus(statusId) || (matchTime || timeLeft)) {
            return (
                <MatchTime
                    isMatchLive={true}
                    sportId={match.sportId}
                    timeLeft={timeLeft}
                    matchTime={matchTime}
                    eventPartName={currentEventPart}
                    currentEventPartParam={currentEventPartParam}
                    timeLeftText={timeLeftText}
                    minuteSign="'"
                    className={bem('Info')}
                />
            );
        }

        return (
            <MatchTime
                isMatchLive={false}
                sportId={match.sportId}
                startTime={startTime}
                className={bem('Info')}
            />
        );
    }

    render() {
        const { match, hasStreamingProviderId, gettext, betRadarEventId } = this.props;

        if (!match) {
            return null;
        }

        const { numberOfMarkets } = match;

        return (
            <span className={bem('Descriptors')}>
                <span className={bem('IndicatorsGroup')}>
                    {hasStreamingProviderId && <GeneralIcon name="video" className={bem('Indicator')} title={gettext('Has streaming video!')}/>}
                    {betRadarEventId && (
                        <div className={bem('StatisticsDiv')}>
                            <BetRadarStatistics useAltIcon betRadarEventId={betRadarEventId}/>
                        </div>
                    )}
                    <EventDescriptorMarketsNumber marketsNumber={numberOfMarkets} className={bem('Indicator')}/>
                </span>
                <span className={bem('InfoGroup')}>
                    {this.getMatchTimeNode()}
                </span>
            </span>
        );
    }
}

const MatchInfoDetails: React.ComponentType<MatchInfoDetailsPublicProps> = compose(
    withTranslation<MatchInfoDetailsPublicProps>(),
    connect(
        (state: AppState, props: MatchInfoDetailsPublicProps): MatchInfoDetailsStoreProps => {
            const slice: MatchListStoreModel | undefined = getSlice<MatchListStoreModel>(state, props.sliceKey);

            const { matchId, matchTimeId, remainingTimeId, currentEventPartId } = props;
            const match: FilteredMatchesWithEvents | undefined = slice && slice.filteredMatchesWithEvents[matchId];

            if (!slice || !match) {
                return {};
            }

            const breakpoints: BreakpointsState | null = state.breakpoints;
            const isSmall: boolean | undefined = breakpoints && breakpoints.small;
            const statistics: FilteredEventStatistics | undefined = slice.filteredEventStatistics.find(statistic => statistic.entityId === matchId);
            const betRadarEventId: string | undefined = statistics?.providerEntityId;

            return {
                match,
                matchTime: matchTimeId && slice.filteredEventInfos[matchTimeId] ?
                    slice.filteredEventInfos[matchTimeId].paramFloat1 : undefined,
                timeLeft: remainingTimeId && slice.filteredEventInfos[remainingTimeId] ?
                    slice.filteredEventInfos[remainingTimeId].paramFloat1 : undefined,
                currentEventPart: currentEventPartId
                    ? slice.filteredEventInfos[currentEventPartId].paramEventPartName1
                    : undefined,
                currentEventPartParam: currentEventPartId && slice.filteredEventInfos[currentEventPartId] ?
                    slice.filteredEventInfos[currentEventPartId].paramEventPartId1 : undefined,
                isSmall,
                hasStreamingProviderId:  !!find(slice.filteredStreamingProviderEventRelation, { 'eventId': matchId }),
                betRadarEventId,
            };
        },
    ),
)(MatchInfoDetailsComponent);
export default MatchInfoDetails;
